// Multiselect dropdown field - mode-aware
// Edit/Create: dropdown + pills with remove
// View/Admin-view: read-only pills
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FieldError, FieldWrapper, Select } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import type { BaseFieldProps } from '../../types/fields';

// Pill component for selected items
function Pill({
  label,
  onRemove,
  removable = true,
}: {
  label: string;
  onRemove?: () => void;
  removable?: boolean;
}) {
  return (
    <span
      className="badge badge-primary gap-2 py- rounded-full flex max-w-[220px] items-center"
      title={label}
    >
      <span className="truncate">{label}</span>
      {removable && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="hover:opacity-70 border rounded-full shrink-0"
          aria-label={`Remove ${label}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

export function MultiselectDropdownField({ name, options = [], maxLength }: BaseFieldProps) {
  const { t } = useTranslation('common');
  const { isReadOnly } = useFormMode();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const error = errors[name]?.message as string | undefined;
  const valueArray = Array.isArray(value) ? value : [];

  // Get label for a value
  const getLabel = (val: string): string => options.find((opt) => opt.value === val)?.label || val;

  // Block adding more when max selections reached
  const atMaxSelections = maxLength !== undefined && valueArray.length >= maxLength;
  const availableOptions = options.filter((opt) => !valueArray.includes(opt.value));

  // Read-only: pills without remove buttons
  if (isReadOnly) {
    return (
      <FieldWrapper>
        {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
        <div className="flex flex-wrap gap-2 py-2">
          {valueArray.length > 0 ? (
            valueArray.map((val) => <Pill key={val} label={getLabel(val)} removable={false} />)
          ) : (
            <span className="text-base-content/60">—</span>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // Editable: dropdown + removable pills
  return (
    <FieldWrapper>
      {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        render={({ field }) => {
          const currentValues = Array.isArray(field.value) ? field.value : [];

          const handleSelect = (selectedValue: string) => {
            if (!selectedValue || currentValues.includes(selectedValue)) return;
            field.onChange([...currentValues, selectedValue]);
          };

          const handleRemove = (valToRemove: string) => {
            field.onChange(currentValues.filter((v: string) => v !== valToRemove));
          };

          return (
            <div className="space-y-2">
              {/* Dropdown - always shows placeholder, resets after selection */}
              <Select
                value=""
                onChange={(e) => handleSelect(e.target.value)}
                options={availableOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
                placeholder={t('common.select')}
                error={!!error}
                disabled={availableOptions.length === 0 || atMaxSelections}
                className="rounded-lg"
              />

              {/* Selected pills */}
              {currentValues.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {currentValues.map((val: string) => (
                    <Pill key={val} label={getLabel(val)} onRemove={() => handleRemove(val)} />
                  ))}
                </div>
              )}
            </div>
          );
        }}
      />
      <FieldError message={error} />
    </FieldWrapper>
  );
}
